import React, { useRef, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, Square, Upload, Camera, RotateCcw, Check, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export const VideoRecorder = ({ onVideoCapture, onFileUpload, isProcessing }) => {
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const fileInputRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mode, setMode] = useState('select'); // 'select', 'camera', 'upload'
  const [facingMode, setFacingMode] = useState('environment');
  const [countdown, setCountdown] = useState(null);

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: facingMode,
  };

  const handleStopCapture = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setCapturing(false);
    }
  }, []);

  const handleStartCapture = useCallback(() => {
    setCapturing(true);
    setRecordedChunks([]);

    const stream = webcamRef.current?.stream;
    if (stream) {
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'video/webm',
      });

      mediaRecorderRef.current.addEventListener('dataavailable', ({ data }) => {
        if (data.size > 0) {
          setRecordedChunks((prev) => [...prev, data]);
        }
      });

      mediaRecorderRef.current.start();

      // Auto-stop after 30 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          handleStopCapture();
        }
      }, 30000);
    }
  }, [handleStopCapture]);

  const handleDownload = useCallback(() => {
    if (recordedChunks.length) {
      const blob = new Blob(recordedChunks, { type: 'video/webm' });
      const file = new File([blob], 'recording.webm', { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      onVideoCapture(file);
    }
  }, [recordedChunks, onVideoCapture]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      onFileUpload(file);
    }
  };

  const handleReset = () => {
    setPreviewUrl(null);
    setRecordedChunks([]);
    setMode('select');
  };

  const switchCamera = () => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  };

  // Auto-process after recording stops
  React.useEffect(() => {
    if (!capturing && recordedChunks.length > 0) {
      handleDownload();
    }
  }, [capturing, recordedChunks.length, handleDownload]);

  return (
    <div className="w-full max-w-2xl mx-auto px-2 sm:px-0">
      <AnimatePresence mode="wait">
        {mode === 'select' && !previewUrl && (
          <motion.div
            key="select"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white mb-2">
                CAPTURE THE PROBLEM
              </h2>
              <p className="text-text-secondary text-sm">
                Record or upload a video of your broken device
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setMode('camera')}
                className="relative group p-8 bg-surface border border-border hover:border-primary 
                         transition-colors duration-200 btn-industrial"
                data-testid="record-video-btn"
              >
                <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                <Camera className="w-12 h-12 text-primary mx-auto mb-4" />
                <span className="font-heading text-lg uppercase tracking-wide text-white block">
                  Record Video
                </span>
                <span className="text-text-muted text-xs mt-2 block">
                  Use your camera
                </span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => fileInputRef.current?.click()}
                className="relative group p-8 bg-surface border border-border hover:border-secondary 
                         transition-colors duration-200 btn-industrial"
                data-testid="upload-video-btn"
              >
                <div className="absolute inset-0 bg-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                <Upload className="w-12 h-12 text-secondary mx-auto mb-4" />
                <span className="font-heading text-lg uppercase tracking-wide text-white block">
                  Upload Video
                </span>
                <span className="text-text-muted text-xs mt-2 block">
                  From your device
                </span>
              </motion.button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleFileChange}
              className="hidden"
              data-testid="video-file-input"
            />
          </motion.div>
        )}

        {mode === 'camera' && !previewUrl && (
          <motion.div
            key="camera"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative"
          >
            {/* HUD Frame */}
            <div className="relative aspect-video bg-black overflow-hidden hud-brackets max-h-[60vh]">
              <Webcam
                ref={webcamRef}
                audio={true}
                videoConstraints={videoConstraints}
                className="w-full h-full object-cover"
              />

              {/* Scanning overlay when recording */}
              {capturing && (
                <div className="absolute inset-0 pointer-events-none scanlines">
                  <div className="scanning-beam" />
                </div>
              )}

              {/* Recording indicator */}
              {capturing && (
                <div className="absolute top-4 left-4 flex items-center gap-2 glass px-3 py-1.5">
                  <div className="w-2 h-2 bg-destructive rounded-full animate-pulse" />
                  <span className="font-mono text-xs text-white">REC</span>
                </div>
              )}

              {/* Grid overlay */}
              <div className="absolute inset-0 grid-pattern pointer-events-none" />
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-3 sm:gap-4 mt-4 sm:mt-6">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setMode('select')}
                className="border-border hover:border-primary min-h-[48px] min-w-[48px]"
                data-testid="back-btn"
              >
                <RotateCcw className="w-5 h-5" />
              </Button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={capturing ? handleStopCapture : handleStartCapture}
                className={cn(
                  "w-16 h-16 rounded-full flex items-center justify-center transition-colors",
                  capturing
                    ? "bg-destructive hover:bg-destructive/90"
                    : "bg-primary hover:bg-primary/90 pulse-glow"
                )}
                data-testid="capture-btn"
              >
                {capturing ? (
                  <Square className="w-6 h-6 text-white fill-white" />
                ) : (
                  <Video className="w-6 h-6 text-white" />
                )}
              </motion.button>

              <Button
                variant="outline"
                size="icon"
                onClick={switchCamera}
                className="border-border hover:border-secondary min-h-[48px] min-w-[48px]"
                data-testid="switch-camera-btn"
              >
                <RotateCcw className="w-5 h-5" />
              </Button>
            </div>

            <p className="text-center text-text-muted text-xs mt-4">
              {capturing ? 'Recording... (max 30 seconds)' : 'Tap to start recording'}
            </p>
          </motion.div>
        )}

        {previewUrl && (
          <motion.div
            key="preview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            <div className="relative aspect-video bg-black overflow-hidden hud-brackets max-h-[60vh]">
              <video
                src={previewUrl}
                controls
                className="w-full h-full object-contain"
                data-testid="video-preview"
              />

              {/* Success overlay */}
              <div className="absolute top-4 right-4 flex items-center gap-2 glass px-3 py-1.5">
                <Check className="w-4 h-4 text-success" />
                <span className="font-mono text-xs text-success">CAPTURED</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3 sm:gap-4 mt-4">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={isProcessing}
                className="border-border hover:border-primary min-h-[48px]"
                data-testid="retake-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Retake
              </Button>
            </div>

            {isProcessing && (
              <div className="flex items-center justify-center gap-2 text-secondary">
                <div className="w-4 h-4 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
                <span className="font-mono text-sm">Processing...</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VideoRecorder;
